// v2.0 layout — control chips + one-mode joystick.
// Gated behind ?controlChips=1 URL flag. Coexists with v1.
//
// Layout:
//   ┌──────────────────────────────────────────────┐
//   │ (1)(2)(+)                              [🗑]  │
//   ├──────────────────────────────────────────────┤
//   │ ⤢ Position                        ┌────┐    │
//   │ ↕ Depth      +1.5 in              │ ▲  │    │
//   │ ⤢ Scale     +15%, 30.0"           │◀ ▶ │    │
//   │ ↻ Rotation   +5°                  │ ▼  │    │
//   │ ∠ Camber     -3°                  └────┘    │
//   └──────────────────────────────────────────────┘
//
// One chip selected at a time — determines which of position / depth /
// scale / rotation / camber the joystick's arrows adjust. Left/right
// arrows only appear for position mode; every other mode is up/down
// only.
//
// This file does NOT reuse the v1 JoystickPad because v1 owns its
// own mode state (the center Repeat2 toggle). In v2 the mode is the
// SELECTED CHIP, so the joystick is a plain 4-way d-pad driven from
// props.

import {
  Move,
  ArrowUpDown,
  Maximize2,
  RotateCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { WheelId } from '@/ar/wheel-renderer'
import { formatWheelDiameter } from '@/lib/formatWheelDiameter'
import { MANUAL_ADJUSTMENT_STEPS } from '@/models/placement-state'
import { RepeatButton } from '@/components/repeat-button/repeat-button'

export type ControlMode =
  | 'position'
  | 'depth'
  | 'scale'
  | 'rotation'
  | 'camber'

interface ManualAdjustmentPanelV2Props {
  wheelId: WheelId
  onHeightChange: (delta: number) => void
  onDepthChange: (delta: number) => void
  onHorizontalChange: (delta: number) => void
  onTiltChange: (delta: number) => void
  onYawChange: (delta: number) => void
  onScaleChange: (delta: number) => void
  onDelete: () => void
  /** Camera button — moved from header to panel bottom-right in v2. */
  onCamera?: () => void
  currentTiltAngle?: number
  currentYawAngle?: number
  currentDepth?: number
  currentScale?: number
  originalDiameterInches?: number
  placedWheelIds?: readonly WheelId[]
  maxWheels?: number
  onSelectWheel?: (id: WheelId) => void
  onAddWheel?: () => void
  /** Rotate glyphs (chip icons / labels / values, joystick arrows) 90°
   *  for landscape reading. Container layout unchanged. */
  isLandscape?: boolean
  /** In-AR pinned-DOM rotation (±90, from App's elRotDeg). When set,
   *  the panel's WHOLE content block rotates in place (dimensions
   *  swapped so it still fills the B container) and the internal
   *  layout switches to the opposite orientation's arrangement, so
   *  chips + joystick read upright from the viewer's physical hold.
   *  0 = browser mode / no mismatch (default). */
  rotateInPlaceDeg?: number
}

const M_TO_IN = 39.3701
const RAD_TO_DEG = 180 / Math.PI
const BUTTON_SIZE = 35

export function ManualAdjustmentPanelV2({
  wheelId,
  onHeightChange,
  onDepthChange,
  onHorizontalChange,
  onTiltChange,
  onYawChange,
  onScaleChange,
  onDelete,
  onCamera,
  currentTiltAngle = 0,
  currentYawAngle = 0,
  currentDepth = 0,
  currentScale = 1,
  originalDiameterInches = 26,
  placedWheelIds = [],
  maxWheels = 4,
  onSelectWheel,
  onAddWheel,
  isLandscape = false,
  rotateInPlaceDeg = 0,
}: ManualAdjustmentPanelV2Props) {
  const step = MANUAL_ADJUSTMENT_STEPS
  const [mode, setMode] = useControlMode()
  const rotated = rotateInPlaceDeg !== 0
  // Which internal arrangement to render. When the block is rotated in
  // place, the viewer's effective orientation is the OPPOSITE of the
  // pinned DOM's — a portrait-pinned session physically held landscape
  // renders the landscape arrangement (chips stacked, joystick below).
  const effLandscape = rotated ? !isLandscape : isLandscape

  const scaledDiameter = originalDiameterInches * currentScale

  const chips: ChipData[] = [
    { mode: 'position', icon: <Move size={14} strokeWidth={2} />, label: 'Position', value: null },
    {
      mode: 'depth',
      icon: <ArrowUpDown size={14} strokeWidth={2} />,
      label: 'Depth',
      value: formatSigned(currentDepth * M_TO_IN, 1) + ' in',
    },
    {
      mode: 'scale',
      icon: <Maximize2 size={14} strokeWidth={2} />,
      label: 'Scale',
      // Baseline reads "100%" (1.0 = 100 %). Diameter shown alongside
      // separated by "/" so the operator sees both proportion and
      // absolute inches without parens.
      value:
        Math.round(currentScale * 100) +
        '% / ' +
        formatWheelDiameter(scaledDiameter) +
        '"',
    },
    {
      mode: 'rotation',
      icon: <RotateCw size={14} strokeWidth={2} />,
      label: 'Rotation',
      value: formatSigned(currentYawAngle * RAD_TO_DEG, 0) + '°',
    },
    {
      mode: 'camber',
      icon: <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>∠</span>,
      label: 'Camber',
      value: formatSigned(currentTiltAngle * RAD_TO_DEG, 0) + '°',
    },
  ]

  // Wire the joystick arrows per selected mode.
  const handleUp =
    mode === 'position'
      ? () => onHeightChange(step.height)
      : mode === 'depth'
        ? () => onDepthChange(-step.depth)
        : mode === 'scale'
          ? () => onScaleChange(step.scale)
          : mode === 'rotation'
            ? () => onYawChange(step.yaw)
            : () => onTiltChange(step.tilt)
  const handleDown =
    mode === 'position'
      ? () => onHeightChange(-step.height)
      : mode === 'depth'
        ? () => onDepthChange(step.depth)
        : mode === 'scale'
          ? () => onScaleChange(-step.scale)
          : mode === 'rotation'
            ? () => onYawChange(-step.yaw)
            : () => onTiltChange(-step.tilt)

  // Selector chips + delete + camera live OUTSIDE the panel in v2 (see
  // App.tsx floating icon row + header column). Panel now just carries
  // the chip viewport + joystick.
  void placedWheelIds
  void onSelectWheel
  void onAddWheel
  void maxWheels
  void onDelete
  void onCamera
  return (
    <section
      data-wheel-id={wheelId}
      data-testid="manual-adjustment-panel-v2"
      style={{
        position: 'relative',
        background: 'rgba(0, 0, 0, 0.85)',
        borderRadius: 0,
        padding: '12px 13px',
        color: 'white',
        boxSizing: 'border-box',
        ...(rotated
          ? isLandscape
            ? {
                // Pinned-landscape session physically held portrait.
                // Pre-rotation block: width = B's full height (100dvh),
                // height = B's landscape width (200, B_WIDTH_HOR in
                // App). Rotation swaps the box so it fills B exactly.
                width: '100dvh',
                height: 200,
                transformOrigin: '0 0',
                transform:
                  rotateInPlaceDeg > 0
                    ? 'rotate(90deg) translateY(-100%)'
                    : 'rotate(-90deg) translateX(-100%)',
              }
            : {
                // Pinned-portrait session physically held landscape.
                // Pre-rotation block: width = B's bar height (180),
                // height = B's full width (100dvw).
                width: PANEL_HEIGHT,
                height: '100dvw',
                transformOrigin: '0 0',
                transform:
                  rotateInPlaceDeg > 0
                    ? 'rotate(90deg) translateY(-100%)'
                    : 'rotate(-90deg) translateX(-100%)',
              }
          : {
              // No rotation: landscape fills the parent B container;
              // portrait is the fixed 180-px tall bottom bar.
              height: isLandscape ? '100%' : PANEL_HEIGHT,
            }),
      }}
    >
      <div
        style={{
          display: 'flex',
          // Landscape arrangement stacks ChipViewport above Joystick
          // vertically so both fit inside the narrow side-strip.
          flexDirection: effLandscape ? 'column' : 'row',
          gap: 8,
          alignItems: 'stretch',
          height: '100%',
        }}
      >
        {/* Chip viewport — 40 % of body width + 12 px extra so the chip
         *  labels (e.g. "Scale  100% / 26.0"") don't wrap. Joystick
         *  container takes the remaining space and centers within. */}
        <div
          style={
            effLandscape
              ? { flex: `0 0 ${CHIP_VIEWPORT_H}px`, minHeight: 0 }
              : {
                  flex: '0 0 calc(52% + 12px)',
                  // Full-height flex column that centers the fixed-
                  // height viewport vertically — avoids %-height +
                  // alignSelf chains, which broke scrolling.
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  paddingLeft: 0,
                  paddingRight: 0,
                }
          }
        >
          <ChipViewport
            chips={chips}
            selectedMode={mode}
            onSelect={setMode}
            isLandscape={effLandscape}
            heightPx={CHIP_VIEWPORT_H}
          />
        </div>
        {/* Joystick — takes the remaining 60 %, centered horizontally. */}
        <div
          style={{
            flex: effLandscape ? '0 0 auto' : '1 1 0',
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'center',
          }}
        >
          <Joystick
            mode={mode}
            handleUp={handleUp}
            handleDown={handleDown}
            onLeft={() => onHorizontalChange(step.horizontal)}
            onRight={() => onHorizontalChange(-step.horizontal)}
            isLandscape={effLandscape}
          />
        </div>
      </div>
    </section>
  )
}

/** Fixed panel height (px). Joystick is 3 × 40 + 2 × 4 = 128 tall;
 *  padding 12 + 12 = 24; chip viewport takes the rest. 180 keeps
 *  enough room for four visible chips before the scroll chevrons
 *  need to show. */
export const PANEL_HEIGHT = 180

const CHIP_GAP = 6

/** Mode-selector viewport height — sized to show exactly 3 complete
 *  chips (chip ≈ 36px + 6px gaps) with no partial 4th peeking at the
 *  cut line. Shared by both orientations so VOR and HOR show the same
 *  three chips before scrolling. */
const CHIP_VIEWPORT_H = 124

function ChipViewport({
  chips,
  selectedMode,
  onSelect,
  isLandscape = false,
  heightPx,
}: {
  chips: ChipData[]
  selectedMode: ControlMode
  onSelect: (m: ControlMode) => void
  isLandscape?: boolean
  /** Explicit viewport height. Preferred over %-height chains — the
   *  scroller needs a definite height to actually scroll. Falls back
   *  to 100% when omitted (parent slot has a fixed size). */
  heightPx?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [scrollHint, setScrollHint] = useState<{
    atTop: boolean
    atBottom: boolean
  }>({ atTop: true, atBottom: true })

  const recomputeHint = (): void => {
    const el = ref.current
    if (el == null) return
    const atTop = el.scrollTop <= 1
    const atBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 1
    setScrollHint({ atTop, atBottom })
  }

  // Start at scroll top so the FIRST chips (Position / Depth / Scale)
  // are the visible ones by default. User scrolls DOWN to reveal
  // Rotation / Camber. Just recompute the chevron hint on mount.
  useEffect(() => {
    recomputeHint()
  }, [])

  return (
    <div
      style={{
        position: 'relative',
        height: heightPx ?? '100%',
      }}
    >
      {/* Chevron UP — visible when not at scroll top (i.e. hidden
       *  chips exist above). */}
      {!scrollHint.atTop && (
        <div
          aria-hidden
          data-testid="chip-scroll-up"
          style={{
            position: 'absolute',
            top: -5,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            color: 'rgba(255,255,255,0.7)',
            zIndex: 1,
          }}
        >
          <ChevronUp size={12} strokeWidth={2.5} />
        </div>
      )}
      <div
        ref={ref}
        data-testid="chip-viewport"
        onScroll={recomputeHint}
        style={{
          height: heightPx ?? '100%',
          overflowY: 'scroll',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          // Explicit gesture allowance — the AR dom-overlay can be
          // conservative about which touches it hands to the DOM.
          touchAction: 'pan-y',
          // Right padding keeps the chips from being covered by the
          // scrollbar; matches the left-side padding on the wrapper
          // for horizontal symmetry.
          paddingRight: 5,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: CHIP_GAP,
            // Fill the full column width instead of capping at 300 —
            // gives each chip room to lay out label + value on one
            // line and, in landscape, more horizontal space after the
            // container's 90° rotation.
            width: '100%',
            paddingRight: 5,
          }}
        >
          {chips.map((c) => (
            <ControlChip
              key={c.mode}
              chip={c}
              selected={selectedMode === c.mode}
              onClick={() => onSelect(c.mode)}
              isLandscape={isLandscape}
            />
          ))}
        </div>
      </div>
      {/* Chevron DOWN — visible when not at scroll bottom (i.e. hidden
       *  chips exist below). */}
      {!scrollHint.atBottom && (
        <div
          aria-hidden
          data-testid="chip-scroll-down"
          style={{
            position: 'absolute',
            bottom: -5,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            color: 'rgba(255,255,255,0.7)',
            zIndex: 1,
          }}
        >
          <ChevronDown size={12} strokeWidth={2.5} />
        </div>
      )}
    </div>
  )
}

interface ChipData {
  mode: ControlMode
  icon: ReactNode
  label: string
  value: string | null
}

function ControlChip({
  chip,
  selected,
  onClick,
}: {
  chip: ChipData
  selected: boolean
  onClick: () => void
  isLandscape?: boolean
}) {
  // No per-label rotation — landscape support rotates the whole
  // ChipViewport as one unit, and per-word rotations inside would
  // stack with the container transform.
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`control-chip-${chip.mode}`}
      aria-pressed={selected}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '10px 12px',
        border: selected
          ? '1px solid rgba(255,255,255,0.75)'
          : '1px solid rgba(255,255,255,0.15)',
        background: selected
          ? 'rgba(255,255,255,0.16)'
          : 'rgba(255,255,255,0.05)',
        borderRadius: 8,
        color: 'white',
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          width: 18,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.85,
        }}
      >
        {chip.icon}
      </span>
      <span style={{ flex: 1, textAlign: 'left' }}>{chip.label}</span>
      {chip.value != null && (
        <span
          style={{
            fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
            fontSize: 12,
            fontWeight: 600,
            opacity: 0.85,
          }}
        >
          {chip.value}
        </span>
      )}
    </button>
  )
}

function Joystick({
  mode,
  handleUp,
  handleDown,
  onLeft,
  onRight,
  isLandscape = false,
}: {
  mode: ControlMode
  handleUp: () => void
  handleDown: () => void
  onLeft: () => void
  onRight: () => void
  isLandscape?: boolean
}) {
  const spacing = 2
  const showLR = mode === 'position'
  // No arrow rotation — the outer B container orients the whole
  // joystick as one unit.
  void isLandscape
  const rot: undefined = undefined
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing,
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 13,
      }}
    >
      <RepeatButton
        onPress={handleUp}
        ariaLabel={`${mode} up`}
        style={{ width: BUTTON_SIZE, height: BUTTON_SIZE }}
      >
        <span style={rot}>▲</span>
      </RepeatButton>
      <div style={{ display: 'flex', gap: spacing }}>
        {showLR ? (
          <RepeatButton
            onPress={onLeft}
            ariaLabel="Move wheel left"
            style={{ width: BUTTON_SIZE, height: BUTTON_SIZE }}
          >
            <span style={rot}>◀</span>
          </RepeatButton>
        ) : (
          <div style={{ width: BUTTON_SIZE, height: BUTTON_SIZE }} />
        )}
        <div style={{ width: BUTTON_SIZE, height: BUTTON_SIZE }} />
        {showLR ? (
          <RepeatButton
            onPress={onRight}
            ariaLabel="Move wheel right"
            style={{ width: BUTTON_SIZE, height: BUTTON_SIZE }}
          >
            <span style={rot}>▶</span>
          </RepeatButton>
        ) : (
          <div style={{ width: BUTTON_SIZE, height: BUTTON_SIZE }} />
        )}
      </div>
      <RepeatButton
        onPress={handleDown}
        ariaLabel={`${mode} down`}
        style={{ width: BUTTON_SIZE, height: BUTTON_SIZE }}
      >
        <span style={rot}>▼</span>
      </RepeatButton>
    </div>
  )
}

function useControlMode(): [ControlMode, (m: ControlMode) => void] {
  const [state, setState] = useState<ControlMode>('position')
  return [state, setState]
}

function formatSigned(value: number, digits: number): string {
  const rounded =
    digits === 0 ? Math.round(value) : Number(value.toFixed(digits))
  if (rounded === 0) return '0'
  const abs = Math.abs(rounded).toFixed(digits)
  return (rounded > 0 ? '+' : '−') + abs
}


